import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth'; 
import { CircularProgress } from '@mui/material';

import './App.css';
import './Main.css';
import Menu from './pages/Menu';
import Plan from './pages/Plan';
import PointReading from './pages/PointReading';
import PointUsing from './pages/PointUsing';
import CardIssuance from './pages/CardIssuance';
import ShopInfo from './pages/ShopInfo';
import ShopImageUpload from './pages/ShopImageUpload';
import CouponList from './pages/CouponList';
import CouponNew from './pages/CouponNew';
import CouponEdit from './pages/CouponEdit';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import NoticeList from './pages/NoticeList';
import NoticeDetail from './pages/NoticeDetail';
import Faq from './pages/Faq';
import TrialExpired from './pages/TrialExpired';
import ShopNew from './pages/ShopNew';

import { auth, db } from './firebase';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';

function App() {
  // ログイン状態を監視(useAuthState)
  const [user, initializing] = useAuthState(auth);
  const [subscriptions, setSubscriptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTrialing, setIsTrialing] = useState(null);
  const [trialEnd, setTrialEnd] = useState(null); // トライアル終了日
  let jsxToRender; 

  // isTrialing取得用useEffect（最小限追加）
  useEffect(() => {
    if (user) {
      setIsTrialing(null);
      const userDocRef = doc(db, 'customers', user.uid);
      const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          if (userData.trial_end) {
            const trialEndTime = userData.trial_end.toMillis();
            const now = Date.now();
            setIsTrialing(now < trialEndTime);
            setTrialEnd(userData.trial_end.toDate()); // トライアル終了日を保持
          } else {
            setIsTrialing(false);
            setTrialEnd(null);
          }
        } else {
          setIsTrialing(false);
          setTrialEnd(null);
        }
      }, (error) => {
        console.error("Error fetching user data: ", error);
        setIsTrialing(false);
      });
      return () => unsubscribe();
    } else {
      setIsTrialing(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      // ログインユーザーの決済情報を取得する
      const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');
      const q = query(subscriptionsRef, where('status', 'in', ['trialing', 'active']));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (snapshot.size === 0) {
          // window.location.href = 'https://buy.stripe.com/test_3cscQ58XBeKI7HW000'
          // 決済が有効でない場合
          setSubscriptions(false);
        } else {
          // 決済が有効な場合
          setSubscriptions(true);
        }
        setLoading(false);
      }, (error) => {
        console.error("Error fetching subscriptions: ", error);
        setLoading(false);
      });
      
      // クリーンナップ関数を設定しておくと、
      // 1.useEffectが次回実行されるタイミング
      // 2.このコンポーネントがアンマウントされるタイミング
      // で実行される
      return () => unsubscribe();
    }
  }, [user, initializing]);

  // if (initializing || subscriptions === null) {
  if (initializing || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }
  
  if(user) {
    if (isTrialing || subscriptions) {
      // 無料トライアル中 OR 決済が有効な場合
      jsxToRender = (
        <div className="App">
          {/* TODO:ヘッダーをここに記述　→ textはstoreで管理して、各コンポーネントでsetしてハンドリング */}
          <Routes>
            <Route path="/" element={<Menu user={user} subscriptions={subscriptions} trialEnd={trialEnd} />}/>

            {/* menu */}
            <Route path="/pointReading" element={<PointReading user={user} />}/>
            <Route path="/cardIssuance" element={<CardIssuance user={user} />}/>
            <Route path="/shopInfo" element={<ShopInfo user={user} />}/>
            <Route path="/shopImageUpload" element={<ShopImageUpload user={user} />}/>
            <Route path="/couponList" element={<CouponList user={user} />}/>

            {/* appbar */}
            <Route path="/noticeList" element={<NoticeList />}/>

            {/* drawer */}
            <Route path="/faq" element={<Faq />}/>
            <Route path="/termsOfService" element={<TermsOfService />}/>
            <Route path="/privacyPolicy" element={<PrivacyPolicy />}/>

            {/* クーポン関連 */}
            <Route path="/couponNew" element={<CouponNew user={user} />}/>
            <Route path="/couponEdit" element={<CouponEdit user={user} />}/>

            {/* お知らせ関連 */}
            <Route path="/noticeDetail/:id" element={<NoticeDetail />}/>

            {/* ポイント関連 */}
            <Route path="/pointUsing" element={<PointUsing />}/>

            {/* 未定義パスはトップへリダイレクト */}
            <Route path="*" element={<Navigate to="/" replace />}/>
          </Routes>
        </div>
      );
    } else if (trialEnd === null) {
      // 新規ユーザー（店舗登録前、trial_end未設定）
      jsxToRender = (
        <div className="App">
          <Routes>
            <Route path="/" element={<ShopNew />}/>
            <Route path="/plan" element={<Plan />}/>
            <Route path="*" element={<Navigate to="/" replace />}/>
          </Routes>
        </div>
      );
    } else {
      // トライアル終了かつ決済情報未登録の場合
      jsxToRender = (
        <div className="App">
          <Routes>
            <Route path="/" element={<TrialExpired user={user} trialEnd={trialEnd} />}/>
            <Route path="/plan" element={<Plan />}/>
            <Route path="*" element={<Navigate to="/" replace />}/>
          </Routes>
        </div>
      );
    }
  } else {
    // ログインしていない場合
    jsxToRender = (
      <div className="App">
        <Routes>
          <Route path="/" element={<SignUp />} />
          <Route path="/login" element={<Login />} />
        </Routes>
      </div>
    );
  }
  return jsxToRender;
}
export default App;